<template>
    <div class="df-header-shell w-full max-w-[100vw]"
        :class="[headerShellClass, (isNavElevated || mobileMenuOpen || logoutConfirmOpen) ? 'z-[80]' : 'z-50']">
        <header ref="headerEl" class="df-header"
            :class="[headerThemeClass, { 'df-header--mega-open': isNavElevated || mobileMenuOpen }]">
            <nav class="w-full max-w-full min-w-0 overflow-x-hidden px-4 sm:px-6 lg:px-10 xl:px-16">
                <div class="flex items-center justify-between h-[50px] max-w-full min-w-0 gap-3">
                    <!-- Logo：透明页眉使用白色 SVG，白底页眉使用黑色 SVG。 -->
                    <router-link ref="logoEl" to="/" class="df-logo flex items-center shrink-0 gap-2"
                        @click.native="closeAllMenus">
                        <img v-if="useDarkLogo" src="../../../static/logo-black.svg" alt="DONGFENG"
                            class="h-7 md:h-8 lg:h-9 w-auto max-w-full">
                        <img v-else src="../../../static/logo.svg" alt="DONGFENG"
                            class="h-7 md:h-8 lg:h-9 w-auto max-w-full">
                    </router-link>

                    <!-- PC 导航 ≥1280：为英文菜单预留足够宽度，避免与右侧操作区重叠。 -->
                    <div ref="pcNavEl"
                        class="hidden xl:flex flex-1 items-center justify-center min-w-0 px-3 2xl:px-8 self-stretch"
                        @mouseleave="onNavAreaLeave">
                        <div v-for="item in navItems" :key="item.name" class="df-nav-item h-full flex items-center"
                            @mouseenter="onNavEnter(item)">
                            <a href="javascript:;"
                                class="df-nav-link text-sm 2xl:text-base whitespace-nowrap px-3 2xl:px-5" :class="{
                                    'font-semibold': isNavActive(item),
                                    'is-active': navHoverKey === item.key,
                                }" @click.prevent="onNavClick(item)">
                                {{ $t(item.name) }}
                            </a>
                        </div>
                    </div>

                    <!-- PC 右侧操作 ≥1280 -->
                    <div ref="pcActionsEl" class="hidden xl:flex items-center shrink-0 gap-4 2xl:gap-5">
                        <button v-if="isCarDetail" type="button" class="df-pill-btn" @click="onPreorder">
                            {{ $t('预订') }}
                        </button>
                        <button type="button" class="df-pill-btn" @click="onTestDrive">
                            {{ $t('预约试驾') }}
                        </button>

                        <!-- 已登录：悬停展开个人中心（与车型/技术 mega 一致） -->
                        <div v-if="userToken" class="df-user-entry flex items-center cursor-pointer gap-2 h-[50px]"
                            :class="{ 'is-active': navHoverKey === 'user' }" @mouseenter="onUserEnter"
                            @mouseleave="onNavAreaLeave">
                            <img :src="user_info.avatar || defaultAvatar" alt="Avatar"
                                class="w-8 h-8 rounded-full object-cover bg-white">
                            <span class="max-w-[7rem] xl:max-w-[10rem] truncate text-sm xl:text-base">
                                {{ userDisplayName }}
                            </span>
                            <i class="el-icon-arrow-down text-xs df-user-entry__arrow"
                                :class="{ 'is-open': navHoverKey === 'user' }"></i>
                        </div>

                        <!-- 未登录 -->
                        <div v-else class="df-user-entry flex items-center cursor-pointer gap-2" @click="goLoginPage">
                            <img :src="defaultAvatar" alt="Avatar" class="w-8 h-8 rounded-full">
                            <span class="text-sm xl:text-base whitespace-nowrap">{{ $t('请登录') }}</span>
                        </div>

                        <!-- 暂时隐藏语言切换入口，保留多语言逻辑以便后续恢复。 -->
                        <el-dropdown v-show="false" trigger="click" @command="onLanguageSelect">
                            <button type="button" class="text-sm xl:text-base whitespace-nowrap" :aria-label="$t('语言')">
                                {{ languageLabel }}
                            </button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item v-for="item in languageOptions" :key="item.value" :command="item.value">
                                    <span :class="{ 'font-semibold': language === item.value }">{{ item.label }}</span>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </div>

                    <!-- H5 / 平板右侧 <1280 -->
                    <div ref="h5ActionsEl" class="flex xl:hidden items-center shrink-0 gap-2.5 sm:gap-3">
                        <!-- 暂时隐藏语言切换入口，保留多语言逻辑以便后续恢复。 -->
                        <el-dropdown v-show="false" trigger="click" @command="onLanguageSelect">
                            <button type="button" class="text-xs font-medium" :aria-label="$t('语言')">
                                {{ language === 'en' ? 'EN' : '中' }}
                            </button>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item v-for="item in languageOptions" :key="item.value" :command="item.value">
                                    <span :class="{ 'font-semibold': language === item.value }">{{ item.label }}</span>
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                        <button type="button" class="df-h5-cta" @click="onTestDrive">
                            {{ $t('预约试驾') }}
                        </button>
                        <button type="button" class="df-h5-menu" :class="{ 'is-open': mobileMenuOpen }"
                            :aria-label="mobileMenuOpen ? $t('关闭菜单') : $t('菜单')" @click="toggleMobileMenu">
                            <span class="df-h5-menu-line"></span>
                            <span class="df-h5-menu-line"></span>
                            <span class="df-h5-menu-line"></span>
                        </button>
                    </div>
                </div>
            </nav>

            <!-- H5 导航列表（Figma 11-导航列表） -->
            <mobile-nav-menu class="xl:hidden" :open="mobileMenuOpen" :logged-in="!!userToken"
                :model-groups="modelGroups" :tech-groups="techGroups" :car-categories="carCategories"
                :selected-car-category="selectedCarCategory" @car-category-select="onCarCategorySelect" @navigate="onMegaNavigate"
                @login="openLoginFromMobile" @logout="onLogout" />

            <!-- PC 巨型下拉（理想汽车式悬停展开） -->
            <nav-mega-panel class="hidden xl:block" :open="megaOpen" :active-key="megaKey" :model-groups="modelGroups"
                :tech-groups="techGroups" :car-categories="carCategories" :selected-car-category="selectedCarCategory"
                @car-category-select="onCarCategorySelect" @mouseenter.native="onMegaEnter" @mouseleave.native="onNavAreaLeave"
                @navigate="onMegaNavigate" @logout="onLogout" />
        </header>

        <!-- 下拉遮罩：点击关闭，参考理想汽车（含无下拉的导航悬停） -->
        <div v-show="megaOpen" class="df-mega-mask hidden xl:block" @click="closeMegaMenu"></div>

        <!-- 单页内容 -->
        <el-dialog custom-class="rounded-lg header-page-dialog" width="60%" title="" :visible.sync="pageShow" center
            :show-close="false" :append-to-body="true">
            <div>
                <div class="text-center text-xl mb-6 text-black font-bold">{{ title }}</div>
                <div class="w-full p-6 bg-F8 rounded-2xl h-screen/50 overflow-y-auto" v-html="pageContent"></div>
                <div class="mt-6 flex justify-center">
                    <button class="h-12 px-20 bg-48324F text-white text-base rounded-lg" @click="pageShow = false">
                        {{ $t('关闭') }}
                    </button>
                </div>
            </div>
        </el-dialog>

        <!-- 登录 -->
        <el-dialog custom-class="rounded-lg header-form-dialog" width="504px" title="" :visible.sync="loginShow" center
            :show-close="false" :append-to-body="true">
            <div>
                <el-form :model="loginForm">
                    <el-form-item label="">
                        <el-tabs v-model="loginForm.activeName" class="flex justify-center">
                            <el-tab-pane :label="$t('邮箱登录')" name="mailbox"></el-tab-pane>
                            <el-tab-pane :label="$t('手机号登录')" name="phone"></el-tab-pane>
                        </el-tabs>
                        <div class="mt-3">
                            <input type="text" class="h-14 border-2 border-eee px-6 w-full rounded-lg text-base"
                                :placeholder="$t('请输入邮箱')" v-model="loginForm.email" v-if="loginForm.activeName == 'mailbox'">

                            <div class="relative" v-if="loginForm.activeName == 'phone'">
                                <el-dropdown trigger="click" class="absolute left-4 top-2" @command="onPrefixSelect">
                                    <div class="w-[70px]">
                                        +{{ prefix }}<i class="el-icon-arrow-down el-icon--right"></i>
                                    </div>
                                    <el-dropdown-menu slot="dropdown">
                                        <div class="h-[200px] overflow-auto">
                                            <el-dropdown-item v-for="(item, index) in nation_list" :key="index"
                                                :command="item.prefix">
                                                <div class="flex items-center justify-between w-[150px]">
                                                    <div>{{ item.area_name }}</div>
                                                    <div class="text-999 text-sm">{{ item.prefix }}</div>
                                                </div>
                                            </el-dropdown-item>
                                        </div>
                                    </el-dropdown-menu>
                                </el-dropdown>
                                <input type="text"
                                    class="h-14 border-2 border-eee pr-6 pl-[102px] w-full rounded-lg text-base"
                                    :placeholder="$t('请输入手机号')" v-model="loginForm.mobile">
                            </div>
                        </div>
                    </el-form-item>
                    <el-form-item label="">
                        <div class="relative">
                            <input :type="loginForm.is_password"
                                class="h-14 border-2 border-eee px-6 w-full rounded-lg text-base"
                                :placeholder="$t('请输入密码(6~12位字母+数字)')" v-model="loginForm.password">
                            <div class="absolute right-4 top-3.5">
                                <img src="../../../static/image/icon/look-no.png" alt="" class="w-6 h-6 cursor-pointer"
                                    v-if="loginForm.is_password == 'password'" @click="loginForm.is_password = 'text'">
                                <img src="../../../static/image/icon/look.png" alt="" class="w-6 h-6 cursor-pointer"
                                    v-else @click="loginForm.is_password = 'password'">
                            </div>
                        </div>
                    </el-form-item>
                </el-form>
                <button class="w-full h-14 bg-48324F text-white text-lg rounded-lg hover:scale-105 duration-500"
                    @click="onLogin">
                    {{ $t('登录') }}
                </button>
                <div class="flex items-center justify-center">
                    <el-button type="text" class="text-black text-base mr-6" @click="goSignupPage">
                        {{ $t('注册账号') }}
                    </el-button>
                    <div class="h-3 w-0.5 bg-999"></div>
                    <el-button type="text" class="text-black text-base ml-6" @click="goForgetPage">
                        {{ $t('忘记密码') }}
                    </el-button>
                </div>
                <div class="flex flex-col items-center mt-10">
                    <div class="mb-4 text-999">{{ $t('其他登录方式') }}</div>
                    <div class="flex">
                        <img :src="item.icon" alt="" v-for="(item, index) in payList" :key="index"
                            class="w-12 h-12 mx-7">
                    </div>
                </div>
                <div class="mt-12 flex justify-center">
                    <el-checkbox v-model="loginForm.checked">
                        <div>
                            <span class="text-999">{{ $t('我已阅读并同意') }}</span>
                            <span class="text-FFA85D" @click.stop="onPage('14')">{{ $t('《用户注册协议》') }}</span>
                            <span class="text-999">{{ $t('与') }}</span>
                            <span class="text-FFA85D" @click.stop="onPage('13')">{{ $t('《隐私政策》') }}</span>
                        </div>
                    </el-checkbox>
                </div>
            </div>
        </el-dialog>

        <!-- 忘记密码 -->
        <el-dialog custom-class="rounded-lg header-form-dialog" width="504px" title="" :visible.sync="forgetShow" center
            :show-close="false" :append-to-body="true">
            <div>
                <div class="flex items-center justify-between mb-8">
                    <i class="el-icon-arrow-left text-xl cursor-pointer" @click="forgetShow = false; goLoginPage()"></i>
                    <div class="text-center text-2xl text-48324F">{{ $t('忘记密码') }}</div>
                    <div></div>
                </div>
                <el-form :model="forgetForm">
                    <el-form-item label="">
                        <input type="text" class="h-14 border-2 border-eee px-6 w-full rounded-lg text-base"
                            :placeholder="$t('请输入邮箱')" v-if="loginForm.activeName == 'mailbox'" v-model="forgetForm.email">
                        <div class="relative" v-if="loginForm.activeName == 'phone'">
                            <input type="text"
                                class="h-14 border-2 border-eee pr-6 pl-[102px] w-full rounded-lg text-base"
                                :placeholder="$t('请输入手机号')" v-model="forgetForm.mobile">
                            <el-dropdown trigger="click" class="absolute left-4 top-2" @command="onPrefixSelect">
                                <div class="w-[70px]">
                                    +{{ prefix }}<i class="el-icon-arrow-down el-icon--right"></i>
                                </div>
                                <el-dropdown-menu slot="dropdown">
                                    <div class="h-[200px] overflow-auto">
                                        <el-dropdown-item v-for="(item, index) in nation_list" :key="index"
                                            :command="item.prefix">
                                            <div class="flex items-center justify-between w-[150px]">
                                                <div>{{ item.area_name }}</div>
                                                <div class="text-999 text-sm">{{ item.prefix }}</div>
                                            </div>
                                        </el-dropdown-item>
                                    </div>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </div>
                    </el-form-item>
                    <el-form-item label="">
                        <div class="relative">
                            <input type="number"
                                class="h-14 border-2 border-eee pl-6 pr-[102px] w-full rounded-lg text-base"
                                :placeholder="$t('请输入验证码')" maxlength="6" @input="limitInputLength($event, 6)"
                                v-model="forgetForm.code">
                            <div class="absolute right-4 top-3.5">
                                <captcha-fetch class="text-base text-right"
                                    @onCode="(key, code) => onCode(key, code, 1)" ref="codePassword"
                                    txtcolor="#794425"></captcha-fetch>
                            </div>
                        </div>
                    </el-form-item>
                    <el-form-item label="">
                        <div class="relative">
                            <input :type="forgetForm.is_password" v-model="forgetForm.password"
                                class="h-14 border-2 border-eee pl-6 pr-[102px] w-full rounded-lg text-base"
                                :placeholder="$t('请输入新密码(6~12位字母+数字)')">
                            <div class="absolute right-4 top-3.5">
                                <img src="../../../static/image/icon/look-no.png" alt="" class="w-6 h-6 cursor-pointer"
                                    v-if="forgetForm.is_password == 'password'"
                                    @click="forgetForm.is_password = 'text'">
                                <img src="../../../static/image/icon/look.png" alt="" class="w-6 h-6 cursor-pointer"
                                    v-else @click="forgetForm.is_password = 'password'">
                            </div>
                        </div>
                    </el-form-item>
                </el-form>
                <button class="w-full h-14 bg-48324F text-white text-lg rounded-lg mt-5" @click="onForgetPassword">
                    {{ $t('确定') }}
                </button>
            </div>
        </el-dialog>

        <!-- 注册 -->
        <el-dialog custom-class="rounded-lg header-form-dialog" width="504px" title="" :visible.sync="registerShow"
            center :show-close="false" :append-to-body="true" top="5vh">
            <div>
                <div class="text-center text-2xl text-48324F mb-8">{{ $t('注册') }}</div>
                <el-form :model="registerForm">
                    <el-form-item label="">
                        <input type="text" class="h-14 border-2 border-eee px-6 w-full rounded-lg text-base"
                            :placeholder="$t('请输入邮箱')" v-if="loginForm.activeName == 'mailbox'" v-model="registerForm.email">
                        <div class="relative" v-if="loginForm.activeName == 'phone'">
                            <input type="text"
                                class="h-14 border-2 border-eee pr-6 pl-[102px] w-full rounded-lg text-base"
                                :placeholder="$t('请输入手机号')" v-model="registerForm.mobile">
                            <el-dropdown trigger="click" class="absolute left-4 top-2" @command="onPrefixSelect">
                                <div class="w-[70px]">
                                    +{{ prefix }}<i class="el-icon-arrow-down el-icon--right"></i>
                                </div>
                                <el-dropdown-menu slot="dropdown">
                                    <div class="h-[200px] overflow-auto">
                                        <el-dropdown-item v-for="(item, index) in nation_list" :key="index"
                                            :command="item.prefix">
                                            <div class="flex items-center justify-between w-[150px]">
                                                <div>{{ item.area_name }}</div>
                                                <div class="text-999 text-sm">{{ item.prefix }}</div>
                                            </div>
                                        </el-dropdown-item>
                                    </div>
                                </el-dropdown-menu>
                            </el-dropdown>
                        </div>
                    </el-form-item>
                    <el-form-item label="">
                        <div class="relative">
                            <input type="number"
                                class="h-14 border-2 border-eee pl-6 pr-[102px] w-full rounded-lg text-base"
                                :placeholder="$t('请输入验证码')" maxlength="6" @input="limitInputLength($event, 6)"
                                v-model="registerForm.code">
                            <div class="absolute right-4 top-3.5">
                                <captcha-fetch class="text-base text-right"
                                    @onCode="(key, code) => onCode(key, code, 2)" ref="codeRegister"
                                    txtcolor="#794425"></captcha-fetch>
                            </div>
                        </div>
                    </el-form-item>
                    <el-form-item label="">
                        <div class="relative">
                            <input :type="registerForm.is_password" v-model="registerForm.password"
                                class="h-14 border-2 border-eee pl-6 pr-[102px] w-full rounded-lg text-base"
                                :placeholder="$t('请输入新密码(6~12位字母+数字)')">
                            <div class="absolute right-4 top-3.5">
                                <img src="../../../static/image/icon/look-no.png" alt="" class="w-6 h-6 cursor-pointer"
                                    v-if="registerForm.is_password == 'password'"
                                    @click="registerForm.is_password = 'text'">
                                <img src="../../../static/image/icon/look.png" alt="" class="w-6 h-6 cursor-pointer"
                                    v-else @click="registerForm.is_password = 'password'">
                            </div>
                        </div>
                    </el-form-item>
                    <el-form-item label="">
                        <el-cascader v-model="regionList" :options="nation_list"
                            :props="{ value: 'id', label: 'area_name', children: 'children' }"
                            class="!w-full h-14 !bg-transparent !border-none" :placeholder="$t('城市地区')" ref="regionCascader"
                            @change="handleChange">
                        </el-cascader>
                    </el-form-item>
                    <el-form-item label="">
                        <input type="text" class="h-14 border-2 border-eee px-6 w-full rounded-lg text-base"
                            :placeholder="$t('请输入护照姓名(英文)')" v-model="registerForm.passport_name">
                    </el-form-item>
                    <el-form-item label="">
                        <input type="text" class="h-14 border-2 border-eee px-6 w-full rounded-lg text-base"
                            :placeholder="$t('请输入使用者姓名(请填写实名)')" v-model="registerForm.user_name">
                    </el-form-item>
                    <el-form-item label="">
                        <div class="relative flex items-center">
                            <el-date-picker v-model="registerForm.birthday" type="datetime" :placeholder="$t('请选择您的生日')"
                                prefix-icon="none" format="yyyy-MM-dd"
                                class="!w-full !bg-transparent !border-none"></el-date-picker>
                            <img src="../../../static/image/icon/birthday.png" alt="" class="w-5 h-5 absolute right-4">
                        </div>
                    </el-form-item>
                </el-form>
                <button class="w-full h-14 bg-48324F text-white text-lg rounded-lg mt-3" @click="onRegister">{{ $t('确定') }}</button>
                <div class="mt-3 flex justify-center">
                    <span class="text-999 pr-1">{{ $t('已有账号，前去登录') }}</span>
                    <span class="text-FFA85D cursor-pointer" @click="registerShow = false; goLoginPage()">{{ $t('登录') }}</span>
                </div>
                <div class="mt-6 flex justify-center">
                    <el-checkbox v-model="registerForm.checked">
                        <div>
                            <span class="text-999">{{ $t('我已阅读并同意') }}</span>
                            <span class="text-FFA85D" @click.stop="onPage('14')">{{ $t('《用户注册协议》') }}</span>
                            <span class="text-999">{{ $t('与') }}</span>
                            <span class="text-FFA85D" @click.stop="onPage('13')">{{ $t('《隐私政策》') }}</span>
                        </div>
                    </el-checkbox>
                </div>
            </div>
        </el-dialog>

        <!-- 退出登录二次确认 -->
        <transition name="df-logout-mask">
            <div v-if="logoutConfirmOpen" class="df-logout-confirm-mask" @click.self="closeLogoutConfirm">
                <div class="df-logout-confirm" role="dialog" aria-modal="true" aria-labelledby="df-logout-title">
                    <h3 id="df-logout-title" class="df-logout-confirm__title">{{ $t('退出登录') }}</h3>
                    <p class="df-logout-confirm__desc">{{ $t('确定退出当前账号吗？') }}</p>
                    <div class="df-logout-confirm__actions">
                        <button type="button" class="df-logout-confirm__btn df-logout-confirm__btn--ghost"
                            @click="closeLogoutConfirm">
                            {{ $t('取消') }}
                        </button>
                        <button type="button" class="df-logout-confirm__btn df-logout-confirm__btn--primary"
                            @click="confirmLogout">
                            {{ $t('确定') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
    import { createMotion, resolveEl } from '@/utils/gsap';
    import CaptchaFetch from '@/components/CaptchaFetch';
    import NavMegaPanel from '@/components/layout/NavMegaPanel';
    import MobileNavMenu from '@/components/layout/MobileNavMenu';
    import { postAccountLogin, postRegion, getSmsSend, postEditPasswordCode, postUserRegister } from '@/api/login';
    import { postCarList, postTechnologyCategory } from '@/api/head';
    import { pageIndex } from '@/api/index';
    import { getLayoutData } from '@/utils/layoutDataCache';

    export default {
        name: 'HeaderPage',
        components: {
            'captcha-fetch': CaptchaFetch,
            'nav-mega-panel': NavMegaPanel,
            'mobile-nav-menu': MobileNavMenu,
        },
        data() {
            return {
                isScrolled: false,
                mobileMenuOpen: false,
                isDark: false,
                language: localStorage.getItem('language') === 'zh-CN' ? 'zh-CN' : 'en', // 当前界面语言，默认英文
                languageOptions: [
                    { value: 'zh-CN', label: '简体中文' },
                    { value: 'en', label: 'English' },
                ], // 支持切换的语言列表
                defaultAvatar: require('../../../static/avatar-default.svg'),
                // 导航：mega=悬停下拉，link=直接跳转（零售中心/服务中心）
                navItems: [
                    { name: '车型', path: '/models', key: 'models', type: 'mega' },
                    { name: '技术', path: '/technology', key: 'technology', type: 'mega' },
                    { name: '零售中心', path: '/retail', key: 'retail', type: 'link' },
                    { name: '服务中心', path: '/service', key: 'service', type: 'link' },
                    { name: '关于我们', path: '/about', key: 'about', type: 'mega' },
                    { name: '其他工具', path: '/tools', key: 'tools', type: 'mega' },
                ],
                megaKey: '',
                megaOpen: false,
                navHoverKey: '',
                megaCloseTimer: null,
                megaSwitchTimer: null, // 已展开下拉菜单切换目标项的延迟定时器
                logoutConfirmOpen: false,
                modelGroups: [], // 页眉车型分组
                carCategories: ['ICE', 'HEV', 'PHEV', 'REEV'], // 页眉车型能源分类
                selectedCarCategory: 'ICE', // 当前选中的车型能源分类
                techGroups: [], // 页眉技术分组

                prefix: '',//默认区号
                nation_list: [],//区号列表

                loginShow: false,//登录弹窗
                loginForm: {
                    activeName: 'phone',
                    password: "",//密码
                    mobile: "",//手机号
                    email: "",//邮箱
                    checked: false,//是否已读用户协议隐私政策
                    is_password: 'password',
                },//登录参数

                forgetShow: false,//忘记密码弹窗
                forgetForm: {
                    mobile: "",//手机号
                    email: "",//邮箱
                    code: '',//验证码
                    password: '',//新密码
                    is_password: 'password',
                },//忘记密码参数

                registerShow: false,//注册弹窗
                registerForm: {
                    mobile: "",//手机号
                    email: "",//邮箱
                    code: '',//验证码
                    password: '',
                    passport_name: '',//护照姓名
                    user_name: '',//用户姓名
                    birthday: '',//出生日期
                    region_id: '', //地区ID
                    area_id: '', //区域ID
                    is_password: 'password',
                    checked: false//是否已读用户协议隐私政策
                },//注册参数

                regionList: [],
                areaList: [],//区域列表

                payList: [
                    { icon: require('../../../static/image/icon/weiChat.png'), name: 'weiChat' },
                    { icon: require('../../../static/image/icon/applePay.png'), name: 'applePay' },
                    { icon: require('../../../static/image/icon/googlePay.png'), name: 'googlePay' }],//支付方式

                userToken: '',//token
                user_info: {},//用户信息

                pageShow: false,//单页弹窗
                pageContent: '',
                title: '',//单页标题
                nationLoaded: false,//区号是否已拉取
            }
        },
        created() {
            // 不放 data，避免 Vue 把 timeline 做成响应式
            this.motion = createMotion();
            this.fetchCarList();
            this.fetchTechnologyGroups();
        },
        mounted() {
            this.initUserData();
            this.initTheme();
            window.addEventListener('scroll', this.handleScroll, { passive: true });
            // 个人信息页已二次确认后直接退出；页眉入口走 onLogout 弹窗确认
            this.$root.$on('auth:logout', this.performLogout);
            // 个人中心更新资料后，重新读取缓存以同步页眉展示
            this.$root.$on('auth:user-info-updated', this.initUserData);

            this.themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            this.themeMediaQuery.addListener(this.checkSystemTheme);
            this.openAuthFromQuery();

            this.$nextTick(() => {
                this.initHeaderMotion();
                this.handleScroll();
            });
        },
        beforeDestroy() {
            window.removeEventListener('scroll', this.handleScroll);
            this.$root.$off('auth:logout', this.performLogout);
            this.$root.$off('auth:user-info-updated', this.initUserData);
            this.clearMegaCloseTimer();
            this.clearMegaSwitchTimer();
            this.unbindMegaScrollBlock();
            this.unlockPageScroll();
            if (this.themeMediaQuery) {
                this.themeMediaQuery.removeListener(this.checkSystemTheme);
            }
            if (this.motion) {
                this.motion.kill();
                this.motion = null;
            }
        },
        watch: {
            isPageLocked(active) {
                if (active) this.lockPageScroll();
                else this.unlockPageScroll();
            },
            // PC 悬停下拉：拦截滚轮，但不改 overflow（overflow:hidden 会让 sticky 页眉失效变“透明”）
            isNavElevated(active) {
                if (!this.$isDesktop) return;
                if (active) this.bindMegaScrollBlock();
                else this.unbindMegaScrollBlock();
            },
            // 路由切换后按当前滚动位置同步 Header 主题
            '$route'() {
                this.closeMegaMenu();
                this.closeMobileMenu();
                this.openAuthFromQuery();
                this.$nextTick(() => {
                    this.handleScroll();
                });
            },
            // 登录/注册/忘记密码弹窗打开时再拉区号，避免浏览页无谓请求
            loginShow(newVal, oldVal) {
                if (newVal) this.ensureNationList();
                if (oldVal === true && newVal === false) {
                    // 检查是否是切换到忘记密码或注册弹窗
                    if (!this.forgetShow && !this.registerShow) {
                        // 如果当前页面不是首页，则跳转到首页
                        if (this.$route.path !== '/') {
                            this.$router.replace({ path: '/' });
                        }
                        // 刷新页面
                        window.location.reload();
                    }
                }
            },
            forgetShow(val) {
                if (val) this.ensureNationList();
            },
            registerShow(val) {
                if (val) this.ensureNationList();
            },
        },
        computed: {
            userDisplayName() {
                return this.user_info.user_name
                    || this.user_info.nickname
                    || this.user_info.user_nickname
                    || '';
            },
            /** 当前语言在页眉中展示的短名称。 */
            languageLabel() {
                const current = this.languageOptions.find(item => item.value === this.language);
                return current ? current.label : 'English';
            },
            /** 是否首页 / 车型详情 / 技术页 / 关于页 / 试驾页 / H5填写信息（透明悬浮导航） */
            isHome() {
                return (
                    this.$route.path === '/'
                    || this.$route.name === 'HomePage'
                    || this.$route.name === 'CarDetail'
                    || this.$route.name === 'TechnologyDetail'
                    || this.$route.name === 'AboutUs'
                    || this.$route.name === 'TestDrive'
                    || (this.$route.name === 'CarReserveOrder' && this.$isMobile)
                );
            },
            /** 是否处于车型详情页，用于控制车型预订入口。 */
            isCarDetail() {
                return this.$route.name === 'CarDetail';
            },
            /** PC 首页：透明悬浮、无占位 */
            isPcHomeHeader() {
                return this.$isDesktop && this.isHome;
            },
            /** H5 / 平板首页：透明悬浮叠在 Hero 上 */
            isMobileHomeHeader() {
                return !this.$isDesktop && this.isHome;
            },
            /** PC 非首页：白底深色字 */
            isPcSolidHeader() {
                return this.$isDesktop && !this.isHome;
            },
            /** 白底页眉显示黑色 Logo；透明页眉显示白色 SVG。 */
            useDarkLogo() {
                return this.isScrolled
                    || this.isPcSolidHeader
                    || this.isNavElevated
                    || this.mobileMenuOpen
                    || this.isOrderOverlayHeader
                    || this.isRetailOverlayHeader;
            },
            /** H5 填写信息页：透明顶栏 + 深色字（叠在浅色 hero 上） */
            isOrderOverlayHeader() {
                return this.$route.name === 'CarReserveOrder' && this.$isMobile;
            },
            /** H5 零售中心 / 服务中心：透明顶栏叠在地图上 */
            isRetailOverlayHeader() {
                return (this.$route.name === 'RetailCenter' || this.$route.name === 'ServiceCenter') && this.$isMobile;
            },
            headerShellClass() {
                // 首页 / H5 零售中心 fixed 不占文档流；其它 sticky 正常占位
                if (this.isPcHomeHeader || this.isMobileHomeHeader || this.isRetailOverlayHeader) {
                    return 'df-header-shell--overlay';
                }
                return 'df-header-shell--flow';
            },
            headerThemeClass() {
                // 导航悬停 / H5 菜单 / 滚动后 / PC 非首页：纯白底 + #1A1A1A
                if (this.isNavElevated || this.mobileMenuOpen || this.isScrolled || this.isPcSolidHeader) {
                    return 'df-header--solid';
                }
                if (this.isOrderOverlayHeader || this.isRetailOverlayHeader) return 'df-header--overlay-light';
                if (this.isPcHomeHeader || this.isMobileHomeHeader) return 'df-header--home';
                return 'df-header--mobile';
            },
            /** 任意导航项悬停（含无下拉的零售/服务中心） */
            isNavElevated() {
                return !!this.navHoverKey || this.megaOpen;
            },
            /** PC 下拉不锁 overflow；仅 H5 菜单锁定页面滚动 */
            isPageLocked() {
                return this.mobileMenuOpen;
            },
        },
        methods: {
            /** 切换界面语言并保存用户选择，刷新页面后保持一致。 */
            onLanguageSelect(language) {
                const languageChanged = this.language !== language;
                this.language = language;
                this.$i18n.locale = language;
                localStorage.setItem('language', language);
                document.title = this.$t('页面标题');
                if (languageChanged) {
                    // 接口内容已按请求语言返回，切换语言后重新获取页眉数据。
                    this.fetchCarList();
                    this.fetchTechnologyGroups();
                }
            },
            /** 获取并整理当前能源分类下的页眉车型菜单数据。 */
            fetchCarList() {
                const carCategory = this.selectedCarCategory;
                getLayoutData(
                    `car-list-${carCategory}`,
                    () => postCarList({ car_category: carCategory }),
                ).then(res => {
                    // 忽略分类切换过程中较晚返回的旧请求，避免车型列表被覆盖。
                    if (carCategory !== this.selectedCarCategory) return;
                    if (res.code !== 1 || !res.data || typeof res.data !== 'object') return;

                    this.modelGroups = Object.keys(res.data)
                        .map(key => res.data[key])
                        .filter(group => group && Array.isArray(group.list) && group.list.length)
                        .map(group => ({
                            title: group.name,
                            items: group.list.map(car => ({
                                id: car.id,
                                name: car.name,
                                desc: car.slogan,
                                // 价格字段为车型列表的标准展示价格，旧字段仅作为兼容兜底。
                                priceNum: car.price !== undefined && car.price !== null && car.price !== ''
                                    ? car.price
                                    : car.starting_price,
                                priceUnit: car.price_unit,
                                image: car.list_image || car.hero_image,
                                path: `/car/${car.id}`,
                            })),
                        }));
                }).catch(() => {
                    if (carCategory !== this.selectedCarCategory) return;
                    // 请求失败时保留空列表，避免展示已废弃的静态车型数据。
                    this.modelGroups = [];
                });
            },
            /** 切换页眉车型能源分类，并按分类重新获取车型列表。 */
            onCarCategorySelect(carCategory) {
                if (!this.carCategories.includes(carCategory) || carCategory === this.selectedCarCategory) return;
                this.selectedCarCategory = carCategory;
                this.fetchCarList();
            },
            /** 获取并整理页眉技术菜单数据 */
            fetchTechnologyGroups() {
                const categories = [
                    { type: 0, title: this.$t('东风自研') },
                    { type: 1, title: this.$t('革新技术') },
                ];

                Promise.all(categories.map(category => getLayoutData(
                    `technology-category-${category.type}`,
                    () => postTechnologyCategory({ type: category.type }),
                )
                    .then(res => ({
                        title: category.title,
                        items: res.code === 1 && Array.isArray(res.data)
                            ? res.data.map(item => ({
                                id: item.id,
                                name: item.name,
                                image: item.image,
                                path: {
                                    path: '/technology',
                                    query: { category_id: item.id },
                                },
                            }))
                            : [],
                    }))
                    .catch(() => ({ title: category.title, items: [] }))))
                    .then(groups => {
                        this.techGroups = groups.filter(group => group.items.length);
                    });
            },
            /**注册*/
            onRegister() {
                let data = {};
                let testEmail = /^[A-Za-z0-9\u4e00-\u9fa5.]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/;
                if (!this.registerForm.checked) return this.$message.warning(this.$t('请阅读并同意遵守《用户协议》'));

                if (this.loginForm.activeName == 'mailbox') {
                    if (!this.registerForm.email) return this.$message.warning(this.$t('请输入邮箱'));
                    if (!testEmail.test(this.registerForm.email)) return this.$message.warning(this.$t('请输入正确的邮箱格式'));
                    data.email = this.registerForm.email;
                } else {
                    if (!this.registerForm.mobile) return this.$message.warning(this.$t('手机号不能为空'));
                    data.mobile = this.registerForm.mobile;
                    data.prefix = this.prefix;
                }

                if (!this.registerForm.code) return this.$message.warning(this.$t('请输入验证码'));
                if (!this.registerForm.password) return this.$message.warning(this.$t('请输入密码'));
                let passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,12}$/;
                if (!passwordRegex.test(this.registerForm.password)) return this.$message.warning(this.$t('密码必须为6~12位字母加数字的组合'));
                if (!this.registerForm.region_id && !this.registerForm.area_id) return this.$message.warning(this.$t('请选择地区'));
                if (!this.registerForm.user_name) return this.$message.warning(this.$t('请输入使用者姓名'));

                data.password = this.registerForm.password;
                data.code = this.registerForm.code;
                data.user_name = this.registerForm.user_name;
                data.region_id = this.registerForm.region_id;
                data.area_id = this.registerForm.area_id;
                if (this.registerForm.passport_name) data.passport_name = this.registerForm.passport_name;
                if (this.registerForm.birthday) data.birthday = this.registerForm.birthday;

                postUserRegister(data).then(res => {
                    if (res.code == 1) {
                        this.$message.success(this.$t('注册成功，去登录'));
                        this.registerShow = false;
                        this.goLoginPage();
                    } else {
                        this.$message.error(res.msg);
                    }
                })
            },
            /**选择地区*/
            handleChange() {
                // 获取级联选择器当前选中的节点对象
                const checkedNodes = this.$refs['regionCascader'].getCheckedNodes();
                // console.log(checkedNodes)
                if (checkedNodes.length > 0) {
                    const node = checkedNodes[0]; // 获取当前选中的节点
                    let path = node.path;
                    // const full_path_name = node.pathLabels.join('/');

                    // this.region = full_path_name;
                    this.registerForm.region_id = path[0];
                    this.registerForm.area_id = path[1];
                }
            },
            /**忘记密码*/
            onForgetPassword() {
                let testEmail = /^[A-Za-z0-9\u4e00-\u9fa5.]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/;
                let testPassword = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{6,12}$/;
                console.log(testPassword.test(this.forgetForm.password))
                let data = {
                    type: 2,
                    password: this.forgetForm.password,
                    code: this.forgetForm.code,
                };

                if (this.loginForm.activeName == 'mailbox') {
                    if (!this.forgetForm.email) return this.$message.warning(this.$t('请输入邮箱'));
                    if (!testEmail.test(this.forgetForm.email)) return this.$message.warning(this.$t('请输入正确的邮箱格式'));
                    data.email = this.forgetForm.email;
                } else {
                    if (!this.forgetForm.mobile) return this.$message.warning(this.$t('请输入手机号'));
                    data.mobile = this.forgetForm.mobile;
                    data.prefix = this.prefix;
                }
                if (!this.forgetForm.code) return this.$message.warning(this.$t('验证码不能为空'));
                if (!this.forgetForm.password) return this.$message.warning(this.$t('密码不能为空'));
                if (!testPassword.test(this.forgetForm.password)) return this.$message.warning(this.$t('密码必须为6~12位字母加数字的组合'));

                postEditPasswordCode(data).then(res => {
                    if (res.code == 1) {
                        this.$message.success(this.$t('修改成功，去登录'));
                        this.forgetShow = false;
                        this.goLoginPage();
                    } else {
                        this.$message.error(res.msg);
                    }
                })
            },
            /**
                 * 验证码回调函数
                 * @param {string} key - 验证码key
                 * @param {string} code - 验证码
                 * @param {number} type - 验证码类型
                 */
            onCode(key, code, type) {
                const requestData = {
                    is_test: 1,
                    key,
                    code
                };

                try {
                    // 根据类型设置请求参数
                    this.setCodeRequestParams(requestData, type);

                    // 发送验证码请求
                    this.sendCodeRequest(requestData, type);
                } catch (error) {
                    // 捕获验证错误
                    console.error('验证码请求参数设置失败:', error.message);
                }
            },
            /**
                 * 设置验证码请求参数
                 * @param {Object} data - 请求数据
                 * @param {number} type - 验证码类型
                 */
            setCodeRequestParams(data, type) {
                let testEmail = /^[A-Za-z0-9\u4e00-\u9fa5.]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/
                if (type == 1) {
                    data.type = 2;
                    if (this.loginForm.activeName == 'mailbox') {
                        if (!this.forgetForm.email) return this.$message.warning(this.$t('请输入邮箱'));
                        if (!testEmail.test(this.forgetForm.email)) return this.$message.warning(this.$t('请输入正确的邮箱格式'));
                        data.email = this.forgetForm.email;
                    } else {
                        if (!this.forgetForm.mobile) {
                            return this.$message.warning(this.$t('请输入手机号'));
                        }
                        data.mobile = this.forgetForm.mobile;
                        data.prefix = this.prefix;
                    }
                } else if (type == 2) {
                    data.type = 1;
                    if (this.loginForm.activeName == 'mailbox') {
                        if (!this.registerForm.email) return this.$message.warning(this.$t('请输入邮箱'));
                        if (!testEmail.test(this.registerForm.email)) return this.$message.warning(this.$t('请输入正确的邮箱格式'));
                        data.email = this.registerForm.email;
                    } else {
                        if (!this.registerForm.mobile) {
                            return this.$message.warning(this.$t('请输入手机号'));
                        }
                        data.mobile = this.registerForm.mobile;
                        data.prefix = this.prefix;
                    }
                }
                return data;
            },

            /**
                 * 发送验证码请求
                 * @param {Object} data - 请求数据
                 * @param {number} type - 验证码类型
                 */
            sendCodeRequest(data, type) {
                getSmsSend(data).then(res => {
                    if (res.code === 1) {
                        this.$message.success(res.msg);
                        // 根据类型赋值验证码
                        this.assignVerificationCode(res.data.code, type);
                        // 开始倒计时
                        this.startCountdown(type);
                    } else {
                        this.$message.error(res.msg);
                        // 重新获取验证码
                        this.refreshCaptcha(type);
                    }
                }).catch(error => {
                    this.$message.error(this.$t('验证码请求失败，请重试'));
                    console.error('验证码请求失败:', error);
                });
            },
            /**
                 * 赋值验证码
                 * @param {string} code - 验证码
                 * @param {number} type - 验证码类型
                 */
            assignVerificationCode(code, type) {
                console.log(code)
                switch (type) {
                    case 1: // 修改密码
                        this.forgetForm.code = code;
                        break;
                    case 2: // 注册
                        this.registerForm.code = code;
                        break;
                }
            },
            /**
                 * 开始倒计时
                 * @param {number} type - 验证码类型
                 */
            startCountdown(type) {
                const refMap = {
                    1: 'codePassword',
                    2: 'codeRegister',
                };

                const refName = refMap[type];
                if (this.$refs[refName]) {
                    this.$refs[refName].start();
                    this.$refs[refName].imgShow = false;
                }
            },
            /**
                 * 刷新验证码
                 * @param {number} type - 验证码类型
                 */
            refreshCaptcha(type) {
                const refMap = {
                    1: 'codePassword',
                    2: 'codeRegister',
                };

                const refName = refMap[type];
                if (this.$refs[refName]) {
                    this.$refs[refName].onCode();
                }
            },

            /**获取单页数据*/
            onPage(id) {
                pageIndex({ type: id })
                    .then(res => {
                        let title = '';
                        switch (id) {
                            case '14':
                                title = '用户协议';
                                break;
                            case '13':
                                title = '隐私政策';
                                break;
                            default:
                                break;
                        }
                        this.title = title;
                        this.pageContent = res.data.content;
                        this.pageShow = true;
                    })
            },
            // 提取初始化逻辑
            initUserData() {
                this.userToken = this.$cookies.get('user_token');
                let user_info = this.$cookies.get('user_info');
                if (user_info) {
                    this.user_info = typeof user_info === 'string' ? JSON.parse(user_info) : user_info;
                } else {
                    this.user_info = {};
                }
            },
            /**选择区号*/
            onPrefixSelect(e) {
                this.prefix = e;
            },
            /**需要区号时再拉取（登录相关弹窗）*/
            ensureNationList() {
                if (this.nationLoaded || (this.nation_list && this.nation_list.length)) return;
                this.onNation();
            },
            /**获取区号*/
            onNation() {
                postRegion({ with_children: 1 }).then(res => {
                    if (res.code == 1) {
                        this.nation_list = res.data || [];
                        this.nationLoaded = true;
                        if (this.nation_list.length) {
                            this.prefix = this.nation_list[0].prefix;
                        }
                    }
                }).catch(() => { });
            },
            /**登录*/
            onLogin() {
                let testPassword = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{6,12}$/;

                let form = {
                    password: this.loginForm.password
                }

                if (this.loginForm.activeName == 'phone') {
                    if (!this.loginForm.mobile) {
                        this.onMsgHint(this.$t('手机号不能为空'), 'warning', 50);
                        return;
                    }
                    form.mobile = this.loginForm.mobile
                    form.prefix = this.prefix
                } else {
                    if (!this.loginForm.email) {
                        this.onMsgHint(this.$t('邮箱不能为空'), 'warning', 50);
                        return;
                    }
                    if (!this.$validate.cnEmailReg(this.loginForm.email)) {
                        this.onMsgHint(this.$t('邮箱错误'), 'warning', 50);
                        return;
                    }
                    form.email = this.loginForm.email
                }

                if (!this.loginForm.password) {
                    this.onMsgHint(this.$t('密码不能为空'), 'warning', 50);
                    return;
                }

                if (!testPassword.test(this.loginForm.password)) return this.$message.warning(this.$t('密码必须为6~12位字母加数字的组合'));

                if (!this.loginForm.checked) {
                    this.onMsgHint(this.$t('请阅读并同意遵守《用户协议》'), 'warning', 50);
                    return;
                }

                postAccountLogin(form).then(res => {
                    if (res.code == 1) {
                        this.onMsgHint(this.$t('登录成功'), 'success', 50);
                        this.$cookies.set('user_info', JSON.stringify(res.data.userinfo), { expires: 15 }); // 7天后过期
                        this.$cookies.set('user_token', res.data.userinfo.user_token, { expires: 15 }); // 7天后过期
                        // console.log(res.data);
                        this.initUserData();
                        this.loginShow = false;
                        // 重置store中的登录弹窗状态
                        this.$store.commit('SET_LOGIN_SHOW', false);
                    } else {
                        this.onMsgHint(res.msg, 'error', 50);
                    }
                }).catch(err => {
                    console.log(err)
                })
            },
            onMsgHint(message, type, offset) {
                this.$message({
                    message: message,
                    type: type || 'warning',
                    offset: offset || 20
                });
            },
            /**PC 悬停用户区：展开个人中心 mega*/
            onUserEnter() {
                this.clearMegaCloseTimer();
                this.scheduleMegaSwitch({ key: 'user', type: 'mega' });
            },
            /**页眉退出：先二次确认*/
            onLogout() {
                this.closeMegaMenu();
                this.closeMobileMenu();
                this.logoutConfirmOpen = true;
            },
            closeLogoutConfirm() {
                this.logoutConfirmOpen = false;
            },
            confirmLogout() {
                this.logoutConfirmOpen = false;
                this.performLogout();
            },
            /**实际退出登录（清登录态并回首页）*/
            performLogout() {
                this.logoutConfirmOpen = false;
                this.closeMegaMenu();
                this.$cookies.remove('user_token');
                this.$cookies.remove('user_info');
                this.userToken = '';
                this.user_info = {};
                if (this.$store && this.$store.dispatch) {
                    this.$store.dispatch('user/logout').catch(() => { });
                }
                this.closeMobileMenu();
                if (this.$route.path !== '/') {
                    this.$router.replace({ path: '/' }).catch(() => { });
                }
            },
            /**导航点击（车型、技术、关于我们、其他工具一级菜单仅展开下拉；其它导航按原路径跳转）*/
            onNavClick(item) {
                this.closeMobileMenu();
                if (!item) return;
                if (item.key === 'models' || item.key === 'technology' || item.key === 'about' || item.key === 'tools') return;
                if (item.type === 'mega') {
                    // PC 悬停已展开；点击跳转对应栏目页
                    this.closeMegaMenu();
                }
                this.navigateTo(item.path);
            },
            isNavActive(item) {
                if (!item || !item.path) return false;
                if (item.path === '/') return this.$route.path === '/';
                return this.$route.path === item.path || this.$route.path.startsWith(item.path + '/');
            },
            /**PC 悬停进入导航项：已展开的下拉菜单需停留片刻才切换*/
            onNavEnter(item) {
                this.clearMegaCloseTimer();
                if (!item) {
                    this.closeMegaMenu();
                    return;
                }
                this.scheduleMegaSwitch(item);
            },
            /**延迟切换下拉内容，避免移动到菜单内容时误触相邻一级导航*/
            scheduleMegaSwitch(item) {
                if (!item) return;
                this.clearMegaSwitchTimer();
                if (!this.megaOpen || this.navHoverKey === item.key) {
                    this.activateNavItem(item);
                    return;
                }
                this.megaSwitchTimer = setTimeout(() => {
                    this.megaSwitchTimer = null;
                    this.activateNavItem(item);
                }, 220);
            },
            /**应用当前导航项并按其类型展开或收起下拉菜单*/
            activateNavItem(item) {
                if (!item) return;
                this.navHoverKey = item.key;
                if (item.type === 'mega') {
                    this.megaKey = item.key;
                    if (this.megaOpen) return;
                    // 从零售/服务中心等无下拉项快速切回时，等内容 key 落地后再打开，避免动画量到高度 0
                    this.$nextTick(() => {
                        if (this.navHoverKey === item.key) {
                            this.megaOpen = true;
                        }
                    });
                } else {
                    // 零售中心 / 服务中心：保持白底顶栏，不展开下拉或显示全屏遮罩
                    this.megaOpen = false;
                    this.megaKey = '';
                }
            },
            onMegaEnter() {
                this.clearMegaCloseTimer();
                this.clearMegaSwitchTimer();
            },
            onNavAreaLeave() {
                this.clearMegaSwitchTimer();
                this.scheduleMegaClose();
            },
            scheduleMegaClose() {
                this.clearMegaCloseTimer();
                this.megaCloseTimer = setTimeout(() => {
                    this.closeMegaMenu();
                }, 160);
            },
            clearMegaCloseTimer() {
                if (this.megaCloseTimer) {
                    clearTimeout(this.megaCloseTimer);
                    this.megaCloseTimer = null;
                }
            },
            /**清理下拉菜单切换延迟，防止离开后仍切换内容*/
            clearMegaSwitchTimer() {
                if (this.megaSwitchTimer) {
                    clearTimeout(this.megaSwitchTimer);
                    this.megaSwitchTimer = null;
                }
            },
            closeMegaMenu() {
                this.clearMegaCloseTimer();
                this.clearMegaSwitchTimer();
                this.megaOpen = false;
                this.megaKey = '';
                this.navHoverKey = '';
            },
            /** PC 下拉展开时拦截页面滚动（保留 sticky，不用 overflow:hidden） */
            bindMegaScrollBlock() {
                if (this._megaScrollBlocked) return;
                this._megaScrollBlocked = true;
                this._onMegaWheel = (e) => {
                    if (e.target && e.target.closest && e.target.closest('.df-mega__inner')) {
                        return;
                    }
                    e.preventDefault();
                };
                window.addEventListener('wheel', this._onMegaWheel, { passive: false });
                window.addEventListener('touchmove', this._onMegaWheel, { passive: false });
            },
            unbindMegaScrollBlock() {
                if (!this._megaScrollBlocked) return;
                window.removeEventListener('wheel', this._onMegaWheel, { passive: false });
                window.removeEventListener('touchmove', this._onMegaWheel, { passive: false });
                this._onMegaWheel = null;
                this._megaScrollBlocked = false;
            },
            /**下拉展开时锁定全局滚动*/
            lockPageScroll() {
                if (this._scrollLocked) return;
                const docEl = document.documentElement;
                const body = document.body;
                const scrollY = window.pageYOffset || docEl.scrollTop || 0;
                const scrollbarWidth = window.innerWidth - docEl.clientWidth;
                // H5 菜单才用 position:fixed（兼容 iOS）；PC 悬停下拉见 bindMegaScrollBlock
                const useFixedLock = !!this.mobileMenuOpen && !this.$isDesktop;

                this._scrollLocked = true;
                this._usedFixedLock = useFixedLock;
                this._lockedScrollY = scrollY;
                this._prevHtmlOverflow = docEl.style.overflow;
                this._prevBodyOverflow = body.style.overflow;
                this._prevBodyPaddingRight = body.style.paddingRight;

                docEl.style.overflow = 'hidden';
                body.style.overflow = 'hidden';
                if (scrollbarWidth > 0) {
                    body.style.paddingRight = `${scrollbarWidth}px`;
                }

                if (useFixedLock) {
                    this._prevBodyPosition = body.style.position;
                    this._prevBodyTop = body.style.top;
                    this._prevBodyWidth = body.style.width;
                    body.style.position = 'fixed';
                    body.style.top = `-${scrollY}px`;
                    body.style.width = '100%';
                }
            },
            /**关闭下拉后恢复全局滚动*/
            unlockPageScroll() {
                if (!this._scrollLocked) return;
                const docEl = document.documentElement;
                const body = document.body;
                const scrollY = this._lockedScrollY || 0;
                const usedFixedLock = this._usedFixedLock;

                docEl.style.overflow = this._prevHtmlOverflow || '';
                body.style.overflow = this._prevBodyOverflow || '';
                body.style.paddingRight = this._prevBodyPaddingRight || '';

                if (usedFixedLock) {
                    body.style.position = this._prevBodyPosition || '';
                    body.style.top = this._prevBodyTop || '';
                    body.style.width = this._prevBodyWidth || '';
                }

                this._scrollLocked = false;
                this._usedFixedLock = false;
                if (usedFixedLock) {
                    window.scrollTo(0, scrollY);
                }
            },
            closeAllMenus() {
                this.closeMegaMenu();
                this.closeMobileMenu();
            },
            onMegaNavigate(item) {
                this.closeMegaMenu();
                this.closeMobileMenu();
                this.navigateTo(item && item.path ? item.path : item);
            },
            navigateTo(path) {
                if (!path) return;
                const resolved = this.$router.resolve(path);
                if (resolved.route.matched.length) {
                    this.$router.push(path).catch(err => {
                        if (err.name !== 'NavigationDuplicated') {
                            throw err;
                        }
                    });
                }
            },
            /**预约试驾*/
            onTestDrive() {
                this.closeMobileMenu();
                this.navigateTo('/testdrive');
            },
            /**预订*/
            onPreorder() {
                this.closeMobileMenu();
                const carId = this.$route.params.id;
                if (!carId) return;
                this.$router.push({
                    name: 'CarReserve',
                    params: { id: carId },
                    query: { car_id: carId },
                }).catch(() => { });
            },
            /**跳转登录页*/
            goLoginPage() {
                this.$router.push({ path: '/register' }).catch(() => { });
            },
            /**H5 打开登录*/
            openLoginFromMobile() {
                this.closeMobileMenu();
                this.goLoginPage();
            },
            /**根据 URL query 跳转注册/忘记密码页*/
            openAuthFromQuery() {
                const auth = this.$route.query.auth;
                if (auth === 'register') {
                    this.$router.replace({ path: '/signup' }).catch(() => { });
                } else if (auth === 'forget') {
                    this.forgetShow = false;
                    this.$router.replace({ path: '/forget' }).catch(() => { });
                }
            },
            /**跳转注册页*/
            goSignupPage() {
                this.registerShow = false;
                this.$router.push({ path: '/signup' }).catch(() => { });
            },
            /**跳转忘记密码页*/
            goForgetPage() {
                this.forgetShow = false;
                this.loginShow = false;
                this.$router.push({ path: '/forget' }).catch(() => { });
            },
            /**Header GSAP 初始化*/
            initHeaderMotion() {
                const motion = this.motion;
                if (!motion) return;

                const header = resolveEl(this.$refs.headerEl);
                const logo = resolveEl(this.$refs.logoEl);
                const pcNav = resolveEl(this.$refs.pcNavEl);
                const pcActions = resolveEl(this.$refs.pcActionsEl);
                const h5Actions = resolveEl(this.$refs.h5ActionsEl);

                if (!header) return;

                const navLinks = pcNav ? pcNav.querySelectorAll('.df-nav-link') : [];
                const pillBtns = header.querySelectorAll('.df-pill-btn, .df-h5-cta');
                const userEntry = header.querySelectorAll('.df-user-entry');

                motion.set([logo, ...navLinks, pcActions, h5Actions].filter(Boolean), {
                    autoAlpha: 0,
                    y: -12
                });

                motion.sequence([
                    { targets: logo, autoAlpha: 1, y: 0, duration: 0.45, at: 0.05 },
                    { targets: navLinks, autoAlpha: 1, y: 0, duration: 0.4, stagger: 0.05, at: 0.15 },
                    {
                        targets: [pcActions, h5Actions].filter(Boolean),
                        autoAlpha: 1,
                        y: 0,
                        duration: 0.4,
                        stagger: 0.06,
                        at: 0.28
                    }
                ]);

                motion
                    .hoverLift(navLinks)
                    .hoverScale(pillBtns)
                    .hoverFade(userEntry);

                this.handleScroll();
            },
            handleScroll() {
                const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
                const scrolled = scrollY > 20;
                if (this.isScrolled === scrolled) return;

                this.isScrolled = scrolled;
                const header = resolveEl(this.$refs.headerEl);
                if (!header || !this.motion) return;

                this.motion.to(header, {
                    boxShadow: scrolled && !this.isNavElevated && !this.mobileMenuOpen
                        ? '0 8px 24px rgba(0,0,0,0.08)'
                        : '0 0 0 rgba(0,0,0,0)',
                    duration: 0.35,
                    ease: 'power2.out',
                    overwrite: 'auto'
                });
            },
            toggleMobileMenu() {
                this.mobileMenuOpen = !this.mobileMenuOpen;
                if (this.mobileMenuOpen) this.closeMegaMenu();
            },
            closeMobileMenu() {
                this.mobileMenuOpen = false;
            },
            initTheme() {
                const savedTheme = localStorage.getItem('theme');
                if (savedTheme) {
                    this.applyTheme(savedTheme === 'dark');
                } else {
                    this.checkSystemTheme();
                }
            },
            checkSystemTheme() {
                if (!localStorage.getItem('theme')) {
                    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                    this.applyTheme(systemDark);
                }
            },
            applyTheme(isDark) {
                this.isDark = isDark;
                document.documentElement.classList.toggle('dark', isDark);
            },
            /**限制输入长度*/
            limitInputLength(event, maxLength) {
                const input = event.target;
                if (input.value.length > maxLength) {
                    input.value = input.value.slice(0, maxLength);
                }
            }
        }
    }
</script>

<style>
    /* 注意不要写 scoped，或者确保它能影响到 body 下的元素 */
    .full-height-cascader .el-cascader-menu {
        height: auto !important;
        max-height: 80vh;
        /* 建议设一个较大的上限，防止超出屏幕 */
    }

    /* 隐藏右侧可能出现的滚动条区域 */
    .full-height-cascader .el-cascader-menu__wrap {
        height: auto !important;
    }

    /* H5：弹窗全宽适配 */
    @media (max-width: 767px) {
        .header-page-dialog {
            width: calc(100vw - 32px) !important;
            margin-top: 8vh !important;
        }

        .header-form-dialog {
            width: calc(100vw - 32px) !important;
            max-width: 504px;
            margin-top: 6vh !important;
        }

        .header-page-dialog .el-dialog__body,
        .header-form-dialog .el-dialog__body {
            padding: 24px 16px !important;
        }
    }

    /* 平板：弹窗略收窄 */
    @media (min-width: 768px) and (max-width: 1023px) {
        .header-page-dialog {
            width: 80% !important;
        }

        .header-form-dialog {
            width: min(504px, 86vw) !important;
        }
    }
</style>

<style lang="scss" scoped>
    .df-header-shell--overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
    }

    .df-header-shell--flow {
        position: sticky;
        top: 0;
        left: 0;
        right: 0;
    }

    .df-header {
        position: relative;
        z-index: 70;
        will-change: box-shadow, background-color, color;
        --df-header-fg: #ffffff;
        --df-header-bg: #000000;
        background-color: var(--df-header-bg);
        color: var(--df-header-fg);
        transition: background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
    }

    .df-header--mega-open {
        box-shadow: none !important;
    }

    /* 首页：透明底 + 白字，悬浮无占位（PC / H5 共用） */
    .df-header--home {
        --df-header-fg: #ffffff;
        --df-header-bg: transparent;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.35) 0%, transparent 100%);
        transition: background 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
    }

    /* H5 填写信息：透明底 + 深色字（浅色 hero 上） */
    .df-header--overlay-light {
        --df-header-fg: #1a1a1a;
        --df-header-bg: transparent;
        background: transparent;
        color: #1a1a1a;
    }

    .df-header--overlay-light .df-h5-cta {
        background: rgba(255, 255, 255, 0.6);
        color: #1a1a1a;
        border: none;
    }

    .df-header--overlay-light .df-h5-menu-line {
        background: #1a1a1a;
    }

    /* 白底态（滚动后 / PC 非首页 / 下拉展开）：纯白 + #1A1A1A */
    .df-header--solid {
        --df-header-fg: #1A1A1A;
        --df-header-bg: #ffffff;
        background: #ffffff;
        background-color: #ffffff;
        color: #1A1A1A;
    }

    /* H5 / 平板：沿用黑底白字 */
    .df-header--mobile {
        --df-header-fg: #ffffff;
        --df-header-bg: #000000;
    }

    .df-mega-mask {
        position: fixed;
        top: 50px;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 55;
        background: rgba(0, 0, 0, 0.45);
        cursor: pointer;
    }

    .df-nav-link,
    .df-logo,
    .df-user-entry,
    .df-pill-btn,
    .df-h5-cta {
        will-change: transform, opacity;
    }

    .df-nav-link,
    .df-user-entry {
        color: var(--df-header-fg);
    }

    .df-user-entry.is-active {
        opacity: 1;
    }

    .df-user-entry__arrow {
        transition: transform 0.2s ease;
    }

    .df-user-entry__arrow.is-open {
        transform: rotate(180deg);
    }

    .df-nav-link {
        position: relative;
        display: inline-flex;
        align-items: center;
        height: 100%;
        transition: opacity 0.2s ease;
    }

    .df-nav-link:hover,
    .df-nav-link.is-active {
        opacity: 1;
    }

    .df-nav-link::after {
        content: '';
        position: absolute;
        left: 50%;
        bottom: 0;
        width: 0;
        height: 2px;
        background: currentColor;
        transform: translateX(-50%);
        transition: width 0.25s ease;
    }

    .df-nav-link:hover::after,
    .df-nav-link.is-active::after {
        width: 18px;
    }

    .rotate-180 {
        transform: rotate(180deg);
    }

    .df-pill-btn {
        height: 36px;
        padding: 0 20px;
        border: 1px solid var(--df-header-fg);
        border-radius: 9999px;
        background: transparent;
        color: var(--df-header-fg);
        font-size: 14px;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
    }

    .df-logo-text {
        color: var(--df-header-fg);
        font-size: 18px;
        font-weight: 700;
        letter-spacing: 0.04em;
        line-height: 1;
        white-space: nowrap;
    }

    @media (min-width: 1280px) {
        .df-logo-text {
            font-size: 20px;
        }
    }

    .df-h5-cta {
        height: 32px;
        padding: 0 14px;
        border: none;
        border-radius: 9999px;
        background: #d9d9d9;
        color: #333333;
        font-size: 13px;
        line-height: 1;
        white-space: nowrap;
        cursor: pointer;
    }

    .df-h5-menu {
        width: 32px;
        height: 32px;
        border: none;
        border-radius: 50%;
        background: #d9d9d9;
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 3px;
        cursor: pointer;
        padding: 0;
        position: relative;
    }

    .df-h5-menu-line {
        display: block;
        width: 14px;
        height: 1.5px;
        background: #333333;
        border-radius: 1px;
        transform-origin: center;
        transition: transform 0.25s ease, opacity 0.2s ease;
        will-change: transform, opacity;
    }

    /* 打开时变为关闭叉号（对齐 Figma） */
    .df-h5-menu.is-open {
        background: #f8f8f8;
    }

    .df-h5-menu.is-open .df-h5-menu-line:nth-child(1) {
        transform: translateY(4.5px) rotate(45deg);
    }

    .df-h5-menu.is-open .df-h5-menu-line:nth-child(2) {
        opacity: 0;
    }

    .df-h5-menu.is-open .df-h5-menu-line:nth-child(3) {
        transform: translateY(-4.5px) rotate(-45deg);
    }

    /* 穿透修改对话框样式 */
    ::v-deep .el-dialog__header {
        padding: 0;
    }

    ::v-deep .el-dialog--center .el-dialog__body {
        padding: 40px 32px;
    }

    /* 去除number类型input的步进器（仅作用于当前组件） */
    ::v-deep input[type=number]::-webkit-inner-spin-button,
    ::v-deep input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    ::v-deep input[type=number] {
        -moz-appearance: textfield;
    }

    /* 穿透组件，直接修改内部 input 的样式 */
    ::v-deep .el-input__inner {
        background-color: transparent !important;
        /* 背景透明 */
        /* border: none !important; */
        border: 2px solid #eee !important;
        /* 去掉边框 */
        padding-left: 24px !important;
        /* 如果想让文字更靠左，可以微调 */
        height: 56px !important;
        /* 高度撑满父级 h-14 */
        border-radius: 8px !important;
    }

    /* 调整清空按钮和图标的背景（可选） */
    ::v-deep .el-input__prefix,
    ::v-deep .el-input__suffix {
        background-color: transparent !important;
    }

    /* 针对所有 input 类型（文本、密码、搜索等） */
    input:focus {
        /* 核心：去掉默认轮廓线 */
        outline: none;
        /* 可选：去掉部分浏览器的焦点阴影 */
        box-shadow: none;
        /* 可选：重置焦点时的边框样式（按需调整） */
        border: 1px solid #ccc !important;
        /* 替换为你想要的边框样式 */
    }

    /* 穿透修改折叠面板样式 */
    ::v-deep .el-collapse {
        border: none;
    }

    /* 穿透修改标签页样式 */
    ::v-deep .el-tabs__nav-wrap::after {
        width: 0;
    }

    ::v-deep .el-tabs__item {
        font-size: 20px;
    }

    .df-logout-confirm-mask {
        position: fixed;
        inset: 0;
        z-index: 200;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px;
        background: rgba(26, 26, 26, 0.55);
        box-sizing: border-box;
    }

    .df-logout-confirm {
        width: 360px;
        max-width: 100%;
        padding: 32px 28px 28px;
        border-radius: 12px;
        background: #fff;
        text-align: center;
        box-sizing: border-box;
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.12);
    }

    .df-logout-confirm__title {
        margin: 0;
        font-size: 20px;
        font-weight: 500;
        line-height: 1.4;
        color: #1a1a1a;
    }

    .df-logout-confirm__desc {
        margin: 12px 0 0;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
        color: #666;
    }

    .df-logout-confirm__actions {
        display: flex;
        justify-content: center;
        gap: 16px;
        margin-top: 28px;
    }

    .df-logout-confirm__btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 128px;
        height: 44px;
        padding: 0;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
        cursor: pointer;
        transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
    }

    .df-logout-confirm__btn--ghost {
        border: 1px solid #e5e5e5;
        background: #fff;
        color: #1a1a1a;
    }

    .df-logout-confirm__btn--ghost:hover {
        background: #f8f8f8;
    }

    .df-logout-confirm__btn--primary {
        border: 1px solid #e60021;
        background: #e60021;
        color: #fff;
    }

    .df-logout-confirm__btn--primary:hover {
        background: #cc001c;
        border-color: #cc001c;
    }

    .df-logout-mask-enter-active,
    .df-logout-mask-leave-active {
        transition: opacity 0.22s ease;
    }

    .df-logout-mask-enter-active .df-logout-confirm,
    .df-logout-mask-leave-active .df-logout-confirm {
        transition: transform 0.22s ease, opacity 0.22s ease;
    }

    .df-logout-mask-enter,
    .df-logout-mask-leave-to {
        opacity: 0;
    }

    .df-logout-mask-enter .df-logout-confirm,
    .df-logout-mask-leave-to .df-logout-confirm {
        transform: translateY(10px) scale(0.96);
        opacity: 0;
    }

    @media (max-width: 767px) {
        .df-logout-confirm-mask {
            padding: 6.4vw;
            background: rgba(26, 26, 26, 0.6);
        }

        .df-logout-confirm {
            width: 77.333vw;
            padding: 6.4vw 5.333vw 5.333vw;
            border-radius: 2.133vw;
        }

        .df-logout-confirm__title {
            font-size: 4.267vw;
        }

        .df-logout-confirm__desc {
            margin-top: 2.133vw;
            font-size: 3.467vw;
        }

        .df-logout-confirm__actions {
            gap: 3.2vw;
            margin-top: 5.333vw;
        }

        .df-logout-confirm__btn {
            width: 29.333vw;
            height: 9.6vw;
            border-radius: 1.067vw;
            font-size: 3.733vw;
        }
    }
</style>
